import React from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL deep-linking hooks for pagination (iter551).
 *
 * Tables sync their page / rows-per-page into the URL (?page=2&size=50) so
 * refresh, back/forward, bookmarks and shared links land on the same page.
 * Multi-table pages use distinct keys (e.g. hist_page / audit_page).
 */

/** Integer URL param. Removed from the URL while at its default value. */
export const useUrlIntParam = (key, defaultValue, { replace = true } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const parsed = raw != null ? parseInt(raw, 10) : NaN;
  const value = Number.isFinite(parsed) && parsed > 0 ? parsed : defaultValue;
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const setValue = React.useCallback((next) => {
    const resolved = typeof next === 'function' ? next(valueRef.current) : next;
    // Build from window.location.search (NOT the hook's captured params):
    // react-router's functional updater sees the location from the last
    // render, so two same-tick setters (e.g. PaginationFooter's
    // setItemsPerPage + setPage(1)) would clobber each other's params.
    setSearchParams(() => {
      const p = new URLSearchParams(window.location.search);
      if (resolved == null || resolved === defaultValue) p.delete(key);
      else p.set(key, String(resolved));
      return p;
    }, { replace });
  }, [key, defaultValue, replace, setSearchParams]);
  return [value, setValue];
};

/**
 * URL-backed page number. Always 1-based in the URL; `zeroIndexed: true`
 * exposes 0-based values to callers (server-side pagination code).
 */
export const useUrlPage = (key = 'page', { zeroIndexed = false, defaultPage } = {}) => {
  const defOne = zeroIndexed ? (defaultPage ?? 0) + 1 : (defaultPage ?? 1);
  const [oneBased, setOneBased] = useUrlIntParam(key, defOne, { replace: false });
  const setPage = React.useCallback((next) => {
    if (!zeroIndexed) { setOneBased(next); return; }
    if (typeof next === 'function') setOneBased((cur) => Math.max(0, next(cur - 1)) + 1);
    else setOneBased(Math.max(0, next) + 1);
  }, [zeroIndexed, setOneBased]);
  return [zeroIndexed ? oneBased - 1 : oneBased, setPage];
};

/**
 * URL-backed rows-per-page with localStorage memory. Initial value:
 * URL ?size= wins, else the user's stored preference, else defaultSize.
 * Changing it writes both the URL and localStorage (`pgsize_${scope}`).
 */
export const useUrlPageSize = (scope, defaultSize = 25, key = 'size') => {
  const storageKey = `pgsize_${scope}`;
  const storedDefault = React.useMemo(() => {
    if (typeof window === 'undefined') return defaultSize;
    try {
      const v = parseInt(window.localStorage.getItem(storageKey), 10);
      return Number.isFinite(v) && v > 0 ? v : defaultSize;
    } catch { return defaultSize; }
  }, [storageKey, defaultSize]);
  const [value, setUrlValue] = useUrlIntParam(key, storedDefault, { replace: true });
  const setValue = React.useCallback((next) => {
    const resolved = typeof next === 'function' ? next(value) : next;
    try { window.localStorage.setItem(storageKey, String(resolved)); } catch { /* private mode */ }
    setUrlValue(resolved);
  }, [value, storageKey, setUrlValue]);
  return [value, setValue];
};

/**
 * Search term that survives route navigation (sessionStorage, per browser tab).
 * Restores the last typed search when the user returns to the page; clears
 * when the tab/browser is closed.
 */
export const useSessionSearch = (scope) => {
  const storageKey = `search_${scope}`;
  const [value, setValue] = React.useState(() => {
    try { return window.sessionStorage.getItem(storageKey) || ''; } catch { return ''; }
  });
  const set = React.useCallback((next) => {
    setValue((cur) => {
      const resolved = typeof next === 'function' ? next(cur) : next;
      try {
        if (resolved) window.sessionStorage.setItem(storageKey, resolved);
        else window.sessionStorage.removeItem(storageKey);
      } catch { /* private mode */ }
      return resolved;
    });
  }, [storageKey]);
  return [value, set];
};

/** Merge-patch search params: null/undefined values delete the key. */
export const useSetUrlParams = () => {
  const [, setSearchParams] = useSearchParams();
  return React.useCallback((patch, { replace = true } = {}) => {
    // window.location.search is used for the same same-tick-batching reason
    // documented in useUrlIntParam above.
    setSearchParams(() => {
      const p = new URLSearchParams(window.location.search);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '') p.delete(k);
        else p.set(k, String(v));
      });
      return p;
    }, { replace });
  }, [setSearchParams]);
};

/** Debounce a fast-changing value (search inputs → server fetches). */
export const useDebouncedValue = (value, delayMs = 400) => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
};

/**
 * Effect that skips the initial mount AND only fires when deps actually
 * change (Object.is on each dep). Used for "reset page to 0/1 when a filter
 * changes" effects — running those on mount would wipe the ?page= deep-link
 * the user arrived with. Comparing deps (instead of a first-run flag) keeps
 * it safe under React 18 StrictMode's double-invoked mount effects.
 */
export const useSkipFirstEffect = (fn, deps) => {
  const prevDepsRef = React.useRef(null);
  React.useEffect(() => {
    const prev = prevDepsRef.current;
    prevDepsRef.current = deps;
    if (prev === null) return undefined;
    const unchanged = prev.length === deps.length && prev.every((v, i) => Object.is(v, deps[i]));
    if (unchanged) return undefined;
    return fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
